// "Is this person an agency or an influencer?" answered from the shape of their
// memberships rather than from a flag (ADR-0013).
//
// Two signals, kept apart on purpose. What they *said* — the kind of the
// workspace they own — and what they *do* — how many other people's workspaces
// they were let into, and whether as an outsider. The second is the harder one
// to game, because someone else had to invite them and type their company name.
// Collapsing the two into a single badge would throw away the interesting case,
// which is when they disagree.

import { Link } from "react-router-dom";
import { COMPANY } from "../lib/company";
import { useWorkspace } from "../lib/WorkspaceContext";
import {
  accountShape, OBSERVED_LABEL, WORKSPACE_KIND, type AccountShape,
} from "../lib/workspace";

/** What the two signals together suggest, in the words we would use to them. */
function reading(shape: AccountShape): string {
  if (shape.observed === "new") return "Nothing to go on yet.";
  if (shape.knownAs.length === 1) {
    return `Your clients know you as ${shape.knownAs[0]}.`;
  }
  if (shape.knownAs.length > 1) {
    return `Your clients have you down under ${shape.knownAs.length} different names: `
         + `${shape.knownAs.join(", ")}.`;
  }
  if (shape.observed === "multi-client" || shape.observed === "collaborator") {
    return "You work in other people's workspaces, but none of them recorded a company name for you.";
  }
  if (shape.observed === "employee") {
    return "You work inside a business you do not own.";
  }
  return "You work only in your own workspace.";
}

export function AccountShapeCard() {
  const { workspaces, ready } = useWorkspace();
  if (!ready) return null;

  const shape = accountShape(workspaces);
  if (shape.owned.length === 0 && shape.guest.length === 0 && shape.employed.length === 0) {
    return null;
  }

  return (
    <div className="card">
      <h2>How you use {COMPANY.product}</h2>
      <p className="muted card-sub">
        Read off your workspaces, not stored against your name. A person is not an agency — an
        organisation is — so this is two separate answers rather than one label.
      </p>

      <dl className="kv">
        <dt>You describe yourself as</dt>
        <dd>
          {shape.declared === null && <span className="muted">Not answered yet</span>}
          {shape.declared === "mixed" && (
            <>Different things in different workspaces —{" "}
            {shape.owned.map((w) => w.kind && WORKSPACE_KIND[w.kind].label)
                        .filter(Boolean).join(", ")}</>
          )}
          {shape.declared && shape.declared !== "mixed" && WORKSPACE_KIND[shape.declared].label}
        </dd>

        <dt>What your workspaces show</dt>
        <dd>
          {OBSERVED_LABEL[shape.observed]}
          <div className="muted" style={{ fontSize: ".82rem", marginTop: ".2rem" }}>
            {reading(shape)}
          </div>
        </dd>

        <dt>Workspaces</dt>
        <dd>
          {shape.owned.length === 1 ? "1 you own" : `${shape.owned.length} you own`}
          {shape.employed.length > 0 && `, ${shape.employed.length} you are on the team of`}
          {shape.guest.length > 0 && `, ${shape.guest.length} you work in from outside`}
        </dd>
      </dl>

      {/* The disagreement is the whole point of showing both. */}
      {shape.note && <p className="notice card-sub">{shape.note}</p>}

      {shape.guest.length > 0 && (
        <details className="shape-detail">
          <summary>Which workspaces have you as an outsider</summary>
          <ul className="shape-list">
            {shape.guest.map((w) => (
              <li key={w.workspace_id}>
                <strong>{w.name}</strong>
                {w.organisation && <span className="muted"> · as {w.organisation}</span>}
                {w.roles.length > 0 && <span className="muted"> · {w.roles.join(", ")}</span>}
              </li>
            ))}
          </ul>
        </details>
      )}

      {shape.declared === null && (
        <p className="muted card-sub" style={{ marginBottom: 0 }}>
          Answer the question below and this gets a second opinion to compare against.
        </p>
      )}
      {shape.observed === "multi-client" && (
        <p className="muted card-sub" style={{ marginBottom: 0 }}>
          Each client stays a separate workspace with its own accounts and billing —{" "}
          <Link to="/people">People</Link> in each one shows exactly what you can reach there.
        </p>
      )}
    </div>
  );
}
